import { createSelector } from 'reselect';

import type { ClusterDescription } from '@packages/types/nds/clusterDescription';
import { CloudProvider } from '@packages/types/nds/provider';

import { setClusterDescriptionsFetchIsPending } from '@packages/redux/modules/nds/clusterDescriptionsFetchIsPending';

import * as api from 'js/common/services/api';

// ActionTypes
const SET_CLUSTER_DESCRIPTIONS = 'nds/clusterDescriptions/SET_CLUSTER_DESCRIPTIONS';

type WriteableClusterDescriptionsState = Record<string, { data: ClusterDescription }>;
type ClusterDescriptionsState = Readonly<WriteableClusterDescriptionsState>;

const initialState: ClusterDescriptionsState = {};

// Reducer
// eslint-disable-next-line no-multi-assign
export default function clusterDescriptionsReducer(state = initialState, action): ClusterDescriptionsState {
  switch (action.type) {
    case SET_CLUSTER_DESCRIPTIONS: {
      const payload: Array<ClusterDescription> = action.payload;
      return payload.reduce<WriteableClusterDescriptionsState>((acc, clusterDescription) => {
        acc[clusterDescription.name] = {
          data: clusterDescription,
        };
        return acc;
      }, {});
    }
    default:
      return state;
  }
}

const getClusterDescriptionData = (state): ClusterDescriptionsState => state.nds.clusterDescriptions;

// Selectors
export const getClusterDescriptionsKeyedByName = (state) => getClusterDescriptionData(state);

export const getClusterDescriptionsAsList = createSelector([getClusterDescriptionsKeyedByName], (keyedByName) =>
  Object.values(keyedByName).map((entry) => entry.data)
);

export const getClusterDescription = (state, { clusterName }) => {
  const clusterData = getClusterDescriptionsKeyedByName(state)[clusterName];
  return clusterData ? clusterData.data : null;
};

// Action Creators
const setClusterDescriptions = (payload) => ({
  type: SET_CLUSTER_DESCRIPTIONS,
  payload,
});
export { setClusterDescriptions };

export const loadClusterDescriptions = (groupId: string) => (dispatch) => {
  dispatch(setClusterDescriptionsFetchIsPending(true));
  return api.nds.clusterDescriptions.getClusterDescriptions(groupId).then((response) => {
    const clusterDispatchRes = dispatch(setClusterDescriptions(response));
    dispatch(setClusterDescriptionsFetchIsPending(false));
    return clusterDispatchRes;
  });
};

export const isTenantCluster = (clusterDescription) => {
  return clusterDescription.replicationSpecList
    .flatMap((spec) => spec.regionConfigs)
    .some((config) => config.cloudProvider === CloudProvider.FREE || config.cloudProvider === CloudProvider.SERVERLESS);
};
